import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import ky from "@/shared/api";
import { PaginatedResource, Resource } from "@/shared/types.ts";
import { TariffConfigurationEntity } from "@/features/tariffs-configurations/types.ts";
export * from "./types.ts";

type CreateConfigurationsRequest = {
  title: string;
  isActive: boolean;

  costOfOneCourierVisit: number;
  fuelCostPerOrder: number;
  totalDepreciationPerOrder: number;
  tax: number;
  bankCommission: number;
  plannedMarkup: number;
};

export function useCreateTariffsConfigurations() {
  const queryClient = useQueryClient();

  return useMutation<
    Resource<TariffConfigurationEntity>,
    Error,
    CreateConfigurationsRequest
  >({
    mutationFn: (data: CreateConfigurationsRequest) =>
      ky.post("/api/v1/tariffs/configurations", { json: data }).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tariffs_configurations"] });
    },
  });
}

export function useTariffsConfigurations() {
  return useQuery<PaginatedResource<TariffConfigurationEntity[]>>({
    queryKey: ["tariffs_configurations"],
    queryFn: () =>
      ky
        .get<
          PaginatedResource<TariffConfigurationEntity[]>
        >(`/api/v1/tariffs/configurations`)
        .json(),
  });
}

export function useTariffsConfigurationsById({
  configurationId,
  enabled = true,
}: {
  configurationId: number | string;
  enabled?: boolean;
}) {
  return useQuery<Resource<TariffConfigurationEntity>>({
    queryKey: [`tariffs_configurations_${configurationId}`],
    queryFn: () =>
      ky
        .get<
          Resource<TariffConfigurationEntity>
        >(`/api/v1/tariffs/configurations/${configurationId}`)
        .json(),
    enabled,
  });
}

type UpdateConfigurationsRequest = {
  id: number;
} & CreateConfigurationsRequest;

export function useUpdateTariffsConfigurations({
  onSuccess,
}: {
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();

  return useMutation<
    Resource<TariffConfigurationEntity>,
    Error,
    UpdateConfigurationsRequest
  >({
    mutationFn: (data: UpdateConfigurationsRequest) =>
      ky.put("/api/v1/tariffs/configurations", { json: data }).json(),
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["tariffs_configurations"] });
    },
  });
}

type DeleteTariffsConfigurations = {
  configurationsId: number | string;
};

export function useDeleteTariffsConfigurations({
  onSuccess,
}: {
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();

  return useMutation<
    Resource<TariffConfigurationEntity>,
    Error,
    DeleteTariffsConfigurations
  >({
    mutationFn: (data: DeleteTariffsConfigurations) =>
      ky
        .delete(`/api/v1/tariffs/configurations/${data.configurationsId}`, {
          json: data,
        })
        .json(),
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["tariffs_configurations"] });
    },
  });
}
